import {
  Close as CloseIcon,
  CheckCircle as HighConfidenceIcon,
  Warning as MediumConfidenceIcon,
  Error as LowConfidenceIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Business as OrganizationIcon,
  Factory as FacilityIcon,
  Assignment as ProjectIcon,
  WarningAmber,
  ArrowForward as ArrowForwardIcon,
  Share as RelationIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  ButtonBase,
  Collapse,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import {
  ResearchEntity,
  type FuzzyRubric,
} from "../../../types/researchWorkflow";
import {
  researchApi,
  type EvaluationRunSummary,
  type EvidenceEntry,
  type EvidenceItemData,
  type EvidenceGroupEntry,
  type EvidenceRef,
  type ChainPathNode,
  type ChainEdge,
} from "../../../services/researchApi";
import LogoLoader from "../../common/LogoLoader";
import ScoreCircle from "../shared/ScoreCircle";
import { getScoreChipSx, getScoreLabel } from "../shared/scoreColors";
import CriteriaRadarChart from "../shared/CriteriaRadarChart";

const TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "background.paper",
      color: "text.primary",
      border: "1px solid",
      borderColor: "divider",
      boxShadow: 3,
      "& .MuiTooltip-arrow": {
        color: "background.paper",
        "&::before": {
          border: "1px solid",
          borderColor: "divider",
        },
      },
    },
  },
};

interface TerminalEdgeInfo {
  edge_id: string;
  relation_type: string;
  from_name: string;
  to_name: string;
}

interface DeepEvaluationModalProps {
  open: boolean;
  onClose: () => void;
  entity: ResearchEntity | null;
  criteria: Array<{
    id: string;
    label: string;
    weight: number;
    rubric?: FuzzyRubric;
  }>;
}

interface CriterionResult {
  score: number;
  one_sentence_summary: string;
  summary: string;
  reasoning: string;
  confidence: number;
  evidence_item_ids: string[];
  label?: string;
  description?: string;
  weight?: number;
  criterion_id?: string;
  position?: number;
  // Slim detail payload: ordered citation refs + claim count only. The
  // enriched evidence entries are lazy-loaded per criterion on expansion.
  evidence_refs?: EvidenceRef[];
  evidence_count?: number;
}

interface EvaluationData {
  overall_score: number;
  schema_version: string;
  criteria_results: Record<string, CriterionResult>;
  executive_summary?: {
    overall_assessment: string;
    key_strengths: string[];
    key_concerns: string[];
    recommendation: string;
  };
}

// Parse [cite:item:uuid] / [cite:group:uuid] tokens in LLM-generated text and
// replace them with clickable [N] superscript links.
const renderCitedText = (
  text: string,
  citationNumbers: Map<string, number>,
  onCiteClick: (uuid: string, type: string) => void,
): React.ReactNode => {
  const parts = text.split(/(\[cite:(?:item|group):[^\]]+\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/^\[cite:(item|group):([^\]]+)\]$/);
        if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
        const [, type, uuid] = match;
        const num = citationNumbers.get(uuid);
        if (num === undefined)
          return <React.Fragment key={i}>{part}</React.Fragment>;
        return (
          <Box
            key={i}
            component="span"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onCiteClick(uuid, type);
            }}
            sx={{
              cursor: "pointer",
              color: "primary.main",
              fontWeight: 700,
              fontSize: "0.75em",
              verticalAlign: "super",
              px: 0.25,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            [{num}]
          </Box>
        );
      })}
    </>
  );
};

const DeepEvaluationModal: React.FC<DeepEvaluationModalProps> = ({
  open,
  onClose,
  entity,
  criteria,
}) => {
  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evaluationRuns, setEvaluationRuns] = useState<EvaluationRunSummary[]>(
    [],
  );
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [evidenceExpanded, setEvidenceExpanded] = useState<
    Record<string, boolean>
  >({});
  // Kept across run switches: a criterion's rubric is the same in every run.
  const [rubricExpanded, setRubricExpanded] = useState<
    Record<string, boolean>
  >({});
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  // Lazily-loaded evidence entries, keyed by criterion UUID. Only fetched
  // when a criterion's evidence section is expanded or a citation is clicked.
  const [evidenceByCriterion, setEvidenceByCriterion] = useState<
    Record<string, EvidenceEntry[]>
  >({});
  const [evidenceLoading, setEvidenceLoading] = useState<
    Record<string, boolean>
  >({});
  const [evidenceErrors, setEvidenceErrors] = useState<
    Record<string, string | null>
  >({});
  // Run the detail response actually resolved to — pins lazy evidence
  // fetches to the same output even if a newer evaluation completes.
  const [resolvedRunId, setResolvedRunId] = useState<string | undefined>(
    undefined,
  );
  // Citation whose card should be scrolled into view once its criterion's
  // evidence has loaded into the DOM.
  const [pendingScroll, setPendingScroll] = useState<{
    criterionId: string;
    uuid: string;
    type: string;
  } | null>(null);

  const fetchEvaluationDetail = useCallback(
    (entityId: string, runId?: string) => {
      setIsLoading(true);
      setError(null);
      setEvidenceByCriterion({});
      setEvidenceLoading({});
      setEvidenceErrors({});
      setEvidenceExpanded({});
      setPendingScroll(null);

      researchApi
        .getEvaluationDetail(entityId, runId)
        .then((response) => {
          setEvaluationData(
            response.evaluationData as unknown as EvaluationData,
          );
          setResolvedRunId(runId ?? response.run_id ?? undefined);
        })
        .catch((err) => {
          console.error("Failed to load evaluation detail:", err);
          setError("Failed to load evaluation details");
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [],
  );

  const loadCriterionEvidence = useCallback(
    (criterionId: string) => {
      if (!entity?.id) return;
      setEvidenceLoading((prev) => ({ ...prev, [criterionId]: true }));
      setEvidenceErrors((prev) => ({ ...prev, [criterionId]: null }));
      researchApi
        .getCriterionEvidence(entity.id, criterionId, resolvedRunId)
        .then((evidence) => {
          setEvidenceByCriterion((prev) => ({
            ...prev,
            [criterionId]: evidence[criterionId] ?? [],
          }));
        })
        .catch((err) => {
          console.error("Failed to load criterion evidence:", err);
          setEvidenceErrors((prev) => ({
            ...prev,
            [criterionId]: "Failed to load evidence",
          }));
        })
        .finally(() => {
          setEvidenceLoading((prev) => ({ ...prev, [criterionId]: false }));
        });
    },
    [entity?.id, resolvedRunId],
  );

  const ensureCriterionEvidence = (criterionId: string) => {
    if (
      evidenceByCriterion[criterionId] === undefined &&
      !evidenceLoading[criterionId]
    ) {
      loadCriterionEvidence(criterionId);
    }
  };

  useEffect(() => {
    if (!open || !entity?.id) {
      setEvaluationData(null);
      setError(null);
      setEvaluationRuns([]);
      setSelectedRunId("");
      setEvidenceByCriterion({});
      setEvidenceLoading({});
      setEvidenceErrors({});
      setEvidenceExpanded({});
      setResolvedRunId(undefined);
      setPendingScroll(null);
      return;
    }

    let cancelled = false;

    // Fetch runs list and latest evaluation detail in parallel
    researchApi
      .getEvaluationRuns(entity.id)
      .then((runs) => {
        if (!cancelled) {
          setEvaluationRuns(runs);
          if (runs.length > 0) {
            setSelectedRunId(runs[0].run_id);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load evaluation runs:", err);
        }
      });

    fetchEvaluationDetail(entity.id);

    return () => {
      cancelled = true;
    };
  }, [open, entity?.id, fetchEvaluationDetail]);

  const handleRunChange = (runId: string) => {
    if (!entity?.id || runId === selectedRunId) return;
    setSelectedRunId(runId);
    fetchEvaluationDetail(entity.id, runId);
  };

  const handleCiteClick = (
    criterionId: string,
    uuid: string,
    type: string,
  ) => {
    setEvidenceExpanded((prev) => ({ ...prev, [criterionId]: true }));
    ensureCriterionEvidence(criterionId);
    setPendingScroll({ criterionId, uuid, type });
  };

  // Scroll to the cited card once its criterion's evidence is loaded — the
  // cards only exist in the DOM after the lazy fetch resolves.
  useEffect(() => {
    if (!pendingScroll) return;
    if (evidenceByCriterion[pendingScroll.criterionId] === undefined) return;
    const { uuid, type } = pendingScroll;
    setPendingScroll(null);
    // Give the accordion a beat to expand/render before scrolling.
    window.setTimeout(() => {
      document
        .getElementById(`evidence-${type}-${uuid}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(uuid);
      window.setTimeout(() => setHighlightedId(null), 1800);
    }, 150);
  }, [pendingScroll, evidenceByCriterion]);

  if (!entity) {
    return null;
  }

  if (!open) {
    return null;
  }

  const handleOpenEntityDetail = (entityId: string) => {
    window.open(`/entity/${entityId}`, "_blank");
  };


  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) {
      return (
        <HighConfidenceIcon sx={{ fontSize: 16, color: "success.main" }} />
      );
    }
    if (confidence >= 0.6) {
      return (
        <MediumConfidenceIcon sx={{ fontSize: 16, color: "warning.main" }} />
      );
    }
    return <LowConfidenceIcon sx={{ fontSize: 16, color: "error.main" }} />;
  };

  const getConfidenceLabel = (confidence: number): string => {
    if (confidence >= 0.8) return "High";
    if (confidence >= 0.6) return "Medium";
    return "Low";
  };

  // Truncate summary text for collapsed view
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  const GROUP_TYPE_LABELS: Record<string, string> = {
    relation_chain: "Relation Chain",
  };

  const SOURCE_TYPE_LABELS: Record<string, string> = {
    web: "Web",
    profile_source: "Profile",
    project_source: "Project",
    facility_source: "Facility",
    relation_source: "Relation",
    entity_source: "Entity",
    entity_chain: "Relation Chain",
  };

  // Render a single claim/evidence item card
  const renderClaimCard = (
    evidence: EvidenceItemData,
    terminalEdgeInfo?: TerminalEdgeInfo,
    citationNumber?: number,
  ) => (
    <Box
      key={evidence.id}
      id={`evidence-item-${evidence.id}`}
      sx={{
        backgroundColor: "background.paper",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        overflow: "hidden",
        ...(highlightedId === evidence.id && {
          "@keyframes evidenceGlow": {
            "0%": { boxShadow: "0 0 0 2px rgba(25,118,210,0.9), 0 0 20px 6px rgba(25,118,210,0.35)" },
            "60%": { boxShadow: "0 0 0 2px rgba(25,118,210,0.5), 0 0 14px 4px rgba(25,118,210,0.2)" },
            "100%": { boxShadow: "none" },
          },
          animation: "evidenceGlow 1.8s ease-out forwards",
        }),
      }}
    >
      {/* Header: citation number + all badges */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 0.75,
          backgroundColor: "action.hover",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {citationNumber !== undefined && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.6rem",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {citationNumber}
          </Box>
        )}
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={
              SOURCE_TYPE_LABELS[evidence.source.type] ?? evidence.source.type
            }
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.65rem", height: 18 }}
          />
          {evidence.source_type && (
            <Chip
              label={evidence.source_type}
              size="small"
              variant="outlined"
              color="info"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
          {evidence.evidence_sentiment && (
            <Tooltip
              title={evidence.score_reasoning || ""}
              arrow
              placement="top"
              componentsProps={TOOLTIP_PROPS}
              disableHoverListener={!evidence.score_reasoning}
            >
              <Chip
                label={evidence.evidence_sentiment}
                size="small"
                color={
                  evidence.evidence_sentiment === "supporting"
                    ? "success"
                    : evidence.evidence_sentiment === "contradicting"
                      ? "error"
                      : "default"
                }
                sx={{
                  fontSize: "0.6rem",
                  height: 18,
                  ...(evidence.score_reasoning && { cursor: "help" }),
                }}
              />
            </Tooltip>
          )}
          {evidence.confidence != null && (
            <Chip
              label={`Confidence ${Math.round(evidence.confidence * 100)}%`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.6rem", height: 18 }}
            />
          )}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2">
          {evidence.claim_text}
        </Typography>

        {/* Key Facts */}
        {evidence.key_facts && evidence.key_facts.length > 0 && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                color: "text.disabled",
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Key Facts
            </Typography>
            <Box
              component="ul"
              sx={{
                mt: 0.25,
                mb: 0,
                pl: 2.5,
                "& li": {
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  mb: 0.25,
                },
              }}
            >
              {evidence.key_facts.map((fact: string, idx: number) => (
                <Typography key={idx} component="li" variant="body2">
                  {fact}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Source links */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ fontSize: "0.75rem", color: "text.secondary" }}
          >
            {evidence.source.title}
          </Typography>
          {evidence.source.publish_date && (
            <Chip
              label={new Date(evidence.source.publish_date).toLocaleDateString()}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.75 } }}
            />
          )}
          {evidence.source.url && (
            <Link
              href={evidence.source.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.7rem",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Web
              <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Link>
          )}
          {evidence.source.source_id && (
            <Link
              component="button"
              onClick={() => handleOpenEntityDetail(evidence.source.source_id!)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.7rem",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Source
              <OpenInNewIcon sx={{ fontSize: 11 }} />
            </Link>
          )}
          {evidence.source.type === "relation_source" && terminalEdgeInfo ? (
            <Link
              component="button"
              onClick={() =>
                window.open(`/relation/${terminalEdgeInfo.edge_id}`, "_blank")
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.7rem",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {terminalEdgeInfo.from_name}
              {" → "}
              {terminalEdgeInfo.relation_type.toLowerCase().replace(/_/g, " ")}
              {" → "}
              {terminalEdgeInfo.to_name}
              <RelationIcon sx={{ fontSize: 11 }} />
            </Link>
          ) : evidence.source.entity_id ? (
            <Link
              component="button"
              onClick={() => handleOpenEntityDetail(evidence.source.entity_id!)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.7rem",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {evidence.source.entity_name || "Entity"}
              {evidence.source.type === "facility_source" ? (
                <FacilityIcon sx={{ fontSize: 11 }} />
              ) : evidence.source.type === "project_source" ? (
                <ProjectIcon sx={{ fontSize: 11 }} />
              ) : (
                <OrganizationIcon sx={{ fontSize: 11 }} />
              )}
            </Link>
          ) : null}
        </Box>
      </Box>
    </Box>
  );

  // Render an evidence group card with expandable nested claims
  // Mirrors renderClaimCard structure: text → list → reasoning → badges
  const renderGroupCard = (
    group: EvidenceGroupEntry,
    citationNumber?: number,
  ) => {
    // terminal hop edge — passed to nested claim cards for relation_source links
    const terminalEdge =
      group.chain_edges && group.chain_edges.length > 0
        ? group.chain_edges[group.chain_edges.length - 1]
        : undefined;
    const terminalFromNode =
      group.chain_path && group.chain_path.length >= 2
        ? group.chain_path[group.chain_path.length - 2]
        : undefined;
    const terminalToNode =
      group.chain_path && group.chain_path.length >= 1
        ? group.chain_path[group.chain_path.length - 1]
        : undefined;
    const terminalEdgeInfo: TerminalEdgeInfo | undefined =
      terminalEdge && terminalFromNode && terminalToNode
        ? {
            edge_id: terminalEdge.edge_id,
            relation_type: terminalEdge.relation_type,
            from_name:
              terminalFromNode.entity_name || terminalFromNode.entity_id,
            to_name: terminalToNode.entity_name || terminalToNode.entity_id,
          }
        : undefined;

    return (
      <Box
        key={group.group_id}
        id={`evidence-group-${group.group_id}`}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          ...(highlightedId === group.group_id && {
            "@keyframes evidenceGlow": {
              "0%": { boxShadow: "0 0 0 2px rgba(25,118,210,0.9), 0 0 20px 6px rgba(25,118,210,0.35)" },
              "60%": { boxShadow: "0 0 0 2px rgba(25,118,210,0.5), 0 0 14px 4px rgba(25,118,210,0.2)" },
              "100%": { boxShadow: "none" },
            },
            animation: "evidenceGlow 1.8s ease-out forwards",
          }),
        }}
      >
        {/* Header: citation number + all badges */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 0.75,
            backgroundColor: "action.hover",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {citationNumber !== undefined && (
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.6rem",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {citationNumber}
            </Box>
          )}
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={GROUP_TYPE_LABELS[group.group_type] ?? group.group_type}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
            {group.confidence_score != null && (
              <Chip
                label={`Confidence ${Math.round(group.confidence_score * 100)}%`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.6rem", height: 18 }}
              />
            )}
            <Chip
              label={`${group.evidence_items.length} claim${group.evidence_items.length !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.6rem", height: 18 }}
            />
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 1.5 }}>
          {/* Group label — only shown when chain strip is unavailable */}
          {!(
            group.chain_path &&
            group.chain_path.length >= 2 &&
            group.chain_edges
          ) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", display: "block", mb: 0.5 }}
            >
              {group.label}
            </Typography>
          )}

          {/* Synthesis summary — primary text, mirrors claim_text on individual cards */}
          {group.synthesis_text && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {group.synthesis_text}
            </Typography>
          )}

          {/* Key Findings (same position/style as key_facts) */}
          {group.key_findings && group.key_findings.length > 0 && (
            <Box
              component="ul"
              sx={{
                mt: 0.5,
                mb: 1,
                pl: 2.5,
                "& li": {
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  mb: 0.25,
                },
              }}
            >
              {group.key_findings.map((finding: string, idx: number) => (
                <Typography key={idx} component="li" variant="body2">
                  {finding}
                </Typography>
              ))}
            </Box>
          )}

          {/* Chain path strip — mirrors source link row on claim cards */}
          {group.chain_path &&
            group.chain_path.length >= 2 &&
            group.chain_edges && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {group.chain_path.map((node: ChainPathNode, idx: number) => {
                  const edge: ChainEdge | undefined = group.chain_edges![idx];
                  return (
                    <React.Fragment key={node.entity_id}>
                      <Link
                        component="button"
                        onClick={() => handleOpenEntityDetail(node.entity_id)}
                        sx={{
                          fontSize: "0.7rem",
                          textDecoration: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.25,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {node.entity_name || node.entity_id}
                      </Link>
                      {edge && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                          }}
                        >
                          <ArrowForwardIcon
                            sx={{ fontSize: 11, color: "text.disabled" }}
                          />
                          <Link
                            component="button"
                            onClick={() =>
                              window.open(`/relation/${edge.edge_id}`, "_blank")
                            }
                            sx={{
                              fontSize: "0.65rem",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {edge.relation_type.toLowerCase().replace(/_/g, " ")}
                          </Link>
                          <ArrowForwardIcon
                            sx={{ fontSize: 11, color: "text.disabled" }}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            )}

          {/* Expandable nested claims */}
          <Accordion
            sx={{
              mt: 1,
              backgroundColor: "background.default",
              boxShadow: "none",
              "&:before": { display: "none" },
              border: 1,
              borderColor: "divider",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: "auto",
                "&.Mui-expanded": { minHeight: "auto" },
                "& .MuiAccordionSummary-content": {
                  my: 0.75,
                  "&.Mui-expanded": { my: 0.75 },
                },
              }}
            >
              <Typography
                variant="caption"
                fontWeight="600"
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                Individual Claims
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {group.evidence_items.map((item: EvidenceItemData) =>
                  renderClaimCard(item, terminalEdgeInfo),
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    );
  };

  // Show dialog shell with loading/error/empty states
  if (
    isLoading ||
    error ||
    !evaluationData ||
    !evaluationData.criteria_results
  ) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: "background.paper", backgroundImage: "none" },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Deep Evaluation Results
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            minHeight: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <LogoLoader size="section" surface="paper" label="Loading evaluation details" sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading evaluation details…
              </Typography>
            </Box>
          )}
          {error && <Typography color="error">{error}</Typography>}
          {!isLoading && !error && (
            <Typography color="text.secondary">
              No evaluation data available
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const overallScore = evaluationData.overall_score;
  const criteriaResults = evaluationData.criteria_results;

  // Calculate average confidence from criteria
  const averageConfidence =
    Object.values(criteriaResults).reduce(
      (sum, result) => sum + result.confidence,
      0,
    ) / Object.values(criteriaResults).length;

  // Total individual claims (incl. grouped ones) across all criteria
  const totalClaims = Object.values(criteriaResults).reduce(
    (sum, result) => sum + (result.evidence_count ?? 0),
    0,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "background.paper",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Deep Evaluation Results
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(entity.evaluationData as any)?.spec_version_changed && (
            <Tooltip
              title="Requirements have changed since this evaluation"
              arrow
            >
              <WarningAmber sx={{ fontSize: 20, color: "warning.main" }} />
            </Tooltip>
          )}
          {evaluationRuns.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select
                value={selectedRunId}
                onChange={(e) => handleRunChange(e.target.value as string)}
                size="small"
                sx={{ fontSize: "0.85rem" }}
              >
                {evaluationRuns.map((run) => (
                  <MenuItem key={run.run_id} value={run.run_id}>
                    {run.started_at
                      ? new Date(run.started_at).toLocaleString()
                      : "Unknown date"}
                    {run.spec_version_number != null &&
                      ` · Requirements v${run.spec_version_number}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Executive Snapshot */}
        <Box sx={{ mt: 2, mb: 3 }}>
          {/* Header - Full Width */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <ScoreCircle
              score={overallScore}
              confidence={averageConfidence}
              size="large"
              scoreScale="1-5"
              showConfidence={false}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" fontWeight="600">
                  {entity.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleOpenEntityDetail(entity.kg_entity_id)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Chip
                label={getScoreLabel(overallScore)}
                size="small"
                sx={{ fontWeight: 600, fontSize: "0.8rem", ...getScoreChipSx(overallScore) }}
              />
            </Box>
          </Box>

          {/* Two Column Layout */}
          <Box sx={{ display: "flex", gap: 3, alignItems: "stretch" }}>
            {/* Left Side: Executive Summary */}
            <Box sx={{ flex: 1, display: "flex" }}>
              {/* Executive Summary */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  flex: 1,
                }}
              >
                {evaluationData.executive_summary ? (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Executive Summary
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        {evaluationData.executive_summary.overall_assessment}
                      </Typography>
                      {evaluationData.executive_summary.key_strengths.length >
                        0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            color="success.main"
                            fontWeight="600"
                          >
                            Strengths:
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                            {evaluationData.executive_summary.key_strengths.map(
                              (strength, idx) => (
                                <Typography
                                  key={idx}
                                  component="li"
                                  variant="body2"
                                  sx={{ mb: 0.25, fontSize: "0.85rem" }}
                                >
                                  {strength}
                                </Typography>
                              ),
                            )}
                          </Box>
                        </Box>
                      )}
                      {evaluationData.executive_summary.key_concerns.length >
                        0 && (
                        <Box sx={{ mb: 0 }}>
                          <Typography
                            variant="caption"
                            color="warning.main"
                            fontWeight="600"
                          >
                            Concerns:
                          </Typography>
                          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                            {evaluationData.executive_summary.key_concerns.map(
                              (concern, idx) => (
                                <Typography
                                  key={idx}
                                  component="li"
                                  variant="body2"
                                  sx={{ mb: 0.25, fontSize: "0.85rem" }}
                                >
                                  {concern}
                                </Typography>
                              ),
                            )}
                          </Box>
                        </Box>
                      )}
                      {evaluationData.executive_summary.recommendation && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography
                              variant="caption"
                              color="info.main"
                              fontWeight="600"
                            >
                              Recommendation:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, fontSize: "0.85rem" }}
                            >
                              {evaluationData.executive_summary.recommendation}
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Summary
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                      <Typography
                        component="li"
                        variant="body2"
                        sx={{ mb: 0.5 }}
                      >
                        Overall evaluation score of {overallScore.toFixed(1)}{" "}
                        out of 5.0
                      </Typography>
                      <Typography
                        component="li"
                        variant="body2"
                        sx={{ mb: 0.5 }}
                      >
                        Assessment based on{" "}
                        {Object.keys(criteriaResults).length} evaluation
                        criteria
                      </Typography>
                      <Typography component="li" variant="body2">
                        Average confidence level:{" "}
                        {Math.round(averageConfidence * 100)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Right Side: Radar Chart and Key Findings */}
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <CriteriaRadarChart
                  criteriaResults={criteriaResults}
                  criteria={criteria}
                />
              </Paper>

              {/* Evaluation Metadata */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="600"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  Evaluation Metadata
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Overall score: {overallScore.toFixed(1)} / 5.0
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Criteria evaluated: {Object.keys(criteriaResults).length}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Claims analyzed: {totalClaims}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                    Average confidence: {Math.round(averageConfidence * 100)}%
                  </Typography>
                  {/* The evaluation's own spec version — NOT
                      entity.specVersionNumber, which is the version of the
                      discovery run that last surfaced the candidate and never
                      advances with re-evaluations. */}
                  {((entity.evaluationData as any)?.eval_spec_version_number ??
                    entity.specVersionNumber) && (
                    <Typography component="li" variant="body2">
                      Requirements version: v
                      {(entity.evaluationData as any)?.eval_spec_version_number ??
                        entity.specVersionNumber}
                      {(entity.evaluationData as any)?.spec_version_changed && (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "warning.main",
                            display: "inline-flex",
                            alignItems: "center",
                            verticalAlign: "text-bottom",
                            gap: 0.5,
                            ml: 1,
                            lineHeight: "inherit",
                          }}
                        >
                          <WarningAmber sx={{ fontSize: 16 }} />
                          (latest: v
                          {(entity.evaluationData as any)
                            ?.latest_spec_version_number ?? "?"}
                          )
                        </Typography>
                      )}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Criteria Evaluation */}
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight="600"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mb: 2,
            display: "block",
          }}
        >
          Criteria Evaluation
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Use criteria prop order; results the prop can't match (e.g. an
              evaluation from an older spec version) follow the server-stamped
              requirements-view position instead of raw dict order */}
          {[
            ...criteria
              .filter((c) => criteriaResults[c.id])
              .map(
                (c) =>
                  [c.id, criteriaResults[c.id]] as [string, CriterionResult],
              ),
            ...Object.entries(criteriaResults)
              .filter(([id]) => !criteria.some((c) => c.id === id))
              .sort(
                ([, a], [, b]) =>
                  (a.position ?? Number.MAX_SAFE_INTEGER) -
                  (b.position ?? Number.MAX_SAFE_INTEGER),
              ),
          ].map(([criterionId, result]) => {
            const label =
              result.label || `Criterion ${criterionId.substring(0, 8)}...`;
            const weight = result.weight || 3;
            const description = result.description || "";
            const rubric = criteria.find((c) => c.id === criterionId)?.rubric;
            const rubricScores = ["5", "4", "3", "2", "1"].filter(
              (s) => rubric?.anchors?.[s] !== undefined,
            );
            const rubricOpen = rubricExpanded[criterionId] ?? false;
            const evidenceCount = result.evidence_count ?? 0;
            const criterionEvidence = evidenceByCriterion[criterionId];

            // Build citation number map: uuid → sequential 1-based index.
            // evidence_refs mirrors the order of the lazily-loaded entries,
            // so [N] numbering works before any evidence is fetched.
            const citationNumbers = new Map<string, number>();
            (result.evidence_refs ?? []).forEach(
              (ref: EvidenceRef, idx: number) => {
                citationNumbers.set(ref.id, idx + 1);
              },
            );

            return (
              <Accordion
                key={criterionId}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "background.default",
                  "&:before": { display: "none" },
                  boxShadow: "none",
                }}
              >
                {/* Collapsed View - Always Visible */}
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                      alignItems: "center",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                      pr: 2,
                    }}
                  >
                    {/* Score Circle */}
                    <ScoreCircle
                      score={result.score}
                      confidence={result.confidence}
                      size="small"
                      scoreScale="1-5"
                      showConfidence={false}
                    />

                    {/* Criterion Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        gutterBottom
                      >
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {result.one_sentence_summary ||
                          truncateText(result.summary)}
                      </Typography>
                    </Box>

                    {/* Badges */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Chip
                        label={`Confidence: ${Math.round(result.confidence * 100)}%`}
                        size="small"
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
                      />
                      {evidenceCount > 0 && (
                        <Chip
                          label={`${evidenceCount} claim${evidenceCount !== 1 ? "s" : ""}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                {/* Level 1 Expansion - Criterion Detail */}
                <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Description */}
                  {description && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="600"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Criterion Description
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {description}
                      </Typography>
                      {/* Scoring rubric - a quiet toggle, not a card */}
                      {rubricScores.length > 0 && (
                        <Box sx={{ mt: 0.75 }}>
                          <ButtonBase
                            onClick={() =>
                              setRubricExpanded((prev) => ({
                                ...prev,
                                [criterionId]: !rubricOpen,
                              }))
                            }
                            aria-expanded={rubricOpen}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.25,
                              color: "text.secondary",
                              borderRadius: 0.5,
                              "&:hover": { color: "text.primary" },
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                fontSize: "1.1rem",
                                transform: rubricOpen
                                  ? "rotate(0deg)"
                                  : "rotate(-90deg)",
                                transition: "transform 150ms",
                              }}
                            />
                            <Typography
                              variant="caption"
                              fontWeight="600"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Scoring rubric
                            </Typography>
                          </ButtonBase>
                          <Collapse in={rubricOpen} unmountOnExit>
                            <Box sx={{ mt: 1, pl: 2.5 }}>
                              {rubricScores.map((score) => (
                                <Box
                                  key={score}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    mb: 0.75,
                                  }}
                                >
                                  <Chip
                                    label={score}
                                    size="small"
                                    sx={{
                                      mt: 0.25,
                                      minWidth: 28,
                                      fontWeight: 600,
                                      flexShrink: 0,
                                      ...getScoreChipSx(Number(score)),
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                                  >
                                    {rubric?.anchors[score]}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Full Evaluation */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="600"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Evaluation
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {renderCitedText(
                        result.summary,
                        citationNumbers,
                        (uuid, type) =>
                          handleCiteClick(criterionId, uuid, type),
                      )}
                    </Typography>
                  </Box>

                  {/* Weight Info */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={`Weight: ${weight}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {getConfidenceIcon(result.confidence)}
                      <Typography variant="caption" color="text.secondary">
                        {getConfidenceLabel(result.confidence)} Confidence
                      </Typography>
                    </Box>
                  </Box>

                  {/* Level 2 Nested Expansion - Evidence Items (lazy) */}
                  {evidenceCount > 0 && (
                      <Accordion
                        expanded={evidenceExpanded[criterionId] ?? false}
                        onChange={(_, isExpanded) => {
                          setEvidenceExpanded((prev) => ({
                            ...prev,
                            [criterionId]: isExpanded,
                          }));
                          if (isExpanded) {
                            ensureCriterionEvidence(criterionId);
                          }
                        }}
                        sx={{
                          backgroundColor: "background.paper",
                          boxShadow: "none",
                          "&:before": { display: "none" },
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: "auto",
                            "&.Mui-expanded": { minHeight: "auto" },
                            "& .MuiAccordionSummary-content": {
                              my: 1,
                              "&.Mui-expanded": { my: 1 },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="600"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              View Evidence
                            </Typography>
                            <Chip
                              label={`${evidenceCount} claim${evidenceCount !== 1 ? "s" : ""}`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 1 }}>
                          {evidenceLoading[criterionId] && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                py: 2,
                                justifyContent: "center",
                              }}
                            >
                              <CircularProgress size={18} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Loading evidence…
                              </Typography>
                            </Box>
                          )}
                          {evidenceErrors[criterionId] && (
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ py: 1 }}
                            >
                              {evidenceErrors[criterionId]}
                            </Typography>
                          )}
                          {criterionEvidence && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              {criterionEvidence.map(
                                (entry: EvidenceEntry) => {
                                  const num = citationNumbers.get(
                                    entry.type === "group"
                                      ? entry.group_id
                                      : entry.id,
                                  );
                                  if (entry.type === "group") {
                                    return renderGroupCard(entry, num);
                                  }
                                  return renderClaimCard(
                                    entry,
                                    undefined,
                                    num,
                                  );
                                },
                              )}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default DeepEvaluationModal;
